import React from 'react';

/**
 * SebastianTalk.jsx
 * - シンプルリンクUI（コピー→遷移 / コピーのみ）
 * - コピペ本文は「順序が固定される配列方式」で生成
 */
const CREDIT_TYPES_ORDER = ['Switch', 'PC', 'YouTube', 'VR', 'Phone'];

// プロジェクトに置いたガイドmdのファイル名に合わせてください
const GUIDE_MD_FILENAME = 'sebastian_talk_guide.md';

// 画面コピー時のメッセージ（必要なら調整）
const NOTE_TEXT =
  'このテキストは「セバスチャン・ノート」の状態共有です。' +
  '会話を始めるときは、プロジェクト内のガイドを参照して進めてください。';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatJstDateTimeSeconds(dateObj) {
  // JST (Asia/Tokyo) に変換して YYYY-MM-DD HH:mm:ss
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(dateObj).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

function truncateIsoToSeconds(iso) {
  if (!iso) return '';
  const s = String(iso);
  const noFrac = s.split('.')[0]; // 小数秒以降を切る
  return noFrac;
}

function formatNumber(x, digits = 3) {
  if (x === '' || x == null) return '';
  const n = Number(x);
  if (!Number.isFinite(n)) return String(x);
  return n.toFixed(digits).replace(/0+$/,'').replace(/\.$/,''); // 末尾0は自然に
}

function formatRateArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '[]';
  return `[${arr.map(v => formatNumber(v, 3)).join(',')}]`;
}

async function copyToClipboard(text) {
  // 標準API
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    // フォールバック
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  }
}

/**
 * コピペ本文を順序固定で生成（重要：objectの列挙順に依存しない）
 */
function buildCopipeText({ actor, summaryPayload, logs }) {
  const lines = [];
  const push = (s) => lines.push(s);

  const now = new Date();
  const generatedJst = formatJstDateTimeSeconds(now);

  push('=== SebastianTalk: INPUT (paste as-is) ===');
  push(`actor=${actor || ''}`);
  push(`generated_date_jst=${generatedJst}`);
  push(`note=${NOTE_TEXT}`);

  // --- overview ---
  push('');
  push('--- overview ---');

  const safeLogs = Array.isArray(logs) ? logs : [];
  const recentLogs = safeLogs.filter(r => !r?.is_old && !r?.delete_flag);

  const futureNorms = recentLogs.filter(r => r?.record_type === 'norm' && r?.is_future);
  const undoneNorms = recentLogs.filter(r =>
    r?.record_type === 'norm' &&
    r?.is_future === false &&
    r?.norm_done === false
  );
  const penalties = recentLogs.filter(r => r?.record_type === 'penalty');

  push(`future_norms_count=${futureNorms.length}`);
  push(`undone_norms_count=${undoneNorms.length}`);
  if (undoneNorms.length > 0) {
    const undoneList = undoneNorms.map(n => `${n.content}#${n.id}`).join(',');
    push(`undone_norms=${undoneList}`);
  } else {
    push('undone_norms=');
  }
  push(`penalties_count=${penalties.length}`);

  // --- summary_by_date ---
  push('');
  push('--- summary_by_date (minutes) ---');

  const summary = summaryPayload?.summary || {};
  const dates = Object.keys(summary).sort();

  // 出力順序：date / eff / credit_types...
  // ※ start/end は出さない（不要）
  for (const date of dates) {
    const dayObj = summary[date] || {};
    const eff = dayObj['効率'] ?? dayObj['eff'] ?? 0;

    const parts = [];
    parts.push(`date=${date}`);
    parts.push(`eff=${formatNumber(eff, 3)}`);
    // credit types（固定順）
    for (const t of CREDIT_TYPES_ORDER) {
      const v = dayObj[t] ?? 0;
      parts.push(`${t}=${formatNumber(v, 3)}`);
    }

    push(parts.join(' '));
  }

  // --- records ---
  push('');
  push('--- records (yesterday..today..future, one record per line) ---');

  push(`records_count=${recentLogs.length}`);

  // recordごとに「固定キー順」で出す（KVのMapを作らない）
  for (const r of recentLogs) {
    const parts = [];

    // ここが「見やすさのための順序固定」。
    // 必要なら後で順番や項目の増減を調整できます。
    parts.push(`id=${r.id ?? ''}`);
    parts.push(`record_type=${r.record_type ?? ''}`);

    parts.push(`content=${r.content ?? ''}`);
    parts.push(`approval=${r.approval ?? ''}`);

    // 時刻（秒未満切り捨て）
    parts.push(`timestamp=${truncateIsoToSeconds(r.timestamp)}`);
    parts.push(`registered_time=${truncateIsoToSeconds(r.registered_time)}`);
    parts.push(`start_time=${truncateIsoToSeconds(r.start_time)}`);
    parts.push(`end_time=${truncateIsoToSeconds(r.end_time)}`);
    parts.push(`target_date=${r.target_date ?? ''}`);

    parts.push(`credit_inc_add=${formatNumber(r.credit_inc_add ?? 0, 3)}`);
    parts.push(`credit_inc_other=${formatNumber(r.credit_inc_other ?? 0, 3)}`);
    parts.push(`credit_inc_own=${formatNumber(r.credit_inc_own ?? 0, 3)}`);
    parts.push(`credit_penalty=${formatNumber(r.credit_penalty ?? 0, 3)}`);
    parts.push(`credit_type=${r.credit_type ?? ''}`);
    parts.push(`credit_type2=${r.credit_type2 ?? ''}`);
    parts.push(`effort_amount=${r.effort_amount ?? ''}`);
    parts.push(`effort_minutes=${r.effort_minutes ?? ''}`);
    parts.push(`amount_unit=${r.amount_unit ?? ''}`);

    parts.push(`is_future=${r.is_future ?? false}`);
    parts.push(`locked=${r.locked ?? false}`);
    parts.push(`norm_done=${r.norm_done ?? ''}`);

    parts.push(`penalty_amount=${r.penalty_amount ?? ''}`);
    parts.push(`penalty_rate=${r.penalty_rate ?? ''}`);

    parts.push(`rate_inc_day=${formatRateArray(r.rate_inc_day)}`);

    push(parts.join(' '));
  }

  // --- agent_note (目立たせない) ---
  push('');
  push('--- agent_note ---');
  push(`guide_md=${GUIDE_MD_FILENAME}`);
  push(
    `instruction=プロジェクトに保存されている ${GUIDE_MD_FILENAME} を参照して、セバスチャン・トークとして会話を開始してください。`
  );

  return lines.join('\n');
}

export default function SebastianTalk({
  actor,
  projectUrl,
  summaryPayload,
  logs,
}) {
  const [copyResult, setCopyResult] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const text = React.useMemo(
    () => buildCopipeText({ actor, summaryPayload, logs }),
    [actor, summaryPayload, logs]
  );

  const handleCopyOnly = async (e) => {
    e.preventDefault();
    const ok = await copyToClipboard(text);
    setCopyResult(ok ? 'copied' : 'failed');
    window.setTimeout(() => setCopyResult(null), 2500);
  };

  const handleCopyAndOpen = async (e) => {
    e.preventDefault();
    const ok = await copyToClipboard(text);
    setCopyResult(ok ? 'copied' : 'failed');
    // コピーが失敗しても遷移自体は行う（運用次第で変えてOK）
    if (projectUrl) {
      window.location.href = projectUrl;
    }
  };

  if (!projectUrl) {
    return (
      <div style={{ margin: '0.5rem 0 1rem' }}>
        <span style={{ opacity: 0.7 }}>
          SebastianTalk: プロジェクトURLが未設定です。
        </span>
      </div>
    );
  }

  return (
    <div className="talk_box" style={{ margin: '0.5rem 0 1rem' }}>
      <div className="talk_link">
        <a href={projectUrl} onClick={handleCopyAndOpen}>
          <span className="logo">- Sebastian Talk -</span><br/>
          <span>セバスチャンに聞く</span>
        </a>
      </div>
      <div className="copy_link">
        （<a href="#" onClick={handleCopyOnly}>コピーのみ</a>）
      </div>
      {copyResult === 'failed' && (
        <div className="copy_error">
          コピーに失敗しました
        </div>
      )}
    </div>
  );
}
